// Lander editor (MIS-810 / Task 29).
//
// Loads `GET /api/landers/:id` → { lander }, prepopulates the shared LanderForm,
// and saves via `PATCH /api/landers/:id`. Delete opens a confirm Dialog →
// `DELETE /api/landers/:id` (HARD delete — no soft-delete column) → back to the
// list. Both mutations surface a 403 (viewer-only on the lander's domain) as a
// toast; the API enforces RBAC.
//
// PATCH RPC gotcha: the worker route parses the body with `c.req.json()` (no
// zValidator), so the typed client has no typed `json` for it AND the
// `{ param, json }` form fails typecheck. Send the body through the typed `init`
// escape hatch (`api.api.landers[':id'].$patch({ param }, { init: { ... } })`) —
// useApi()'s fetch preserves the body + Content-Type and adds auth/X-Tenant-Id.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AttachMetaSourcePanel } from '@/ad/components/AttachMetaSourcePanel';
import {
  LanderForm,
  type LanderFormValues,
  landerToFormValues,
} from '@/components/landers/LanderForm';
import { LanderRoutingSection } from '@/components/landers/LanderRoutingSection';
import { buildUpdatePayload } from '@/components/landers/payload';
import type { Lander } from '@/components/landers/types';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';

export const Route = createFileRoute('/_authenticated/landers/$landerId/edit')({
  component: EditLanderPage,
});

function EditLanderPage(): React.ReactElement {
  const { landerId } = Route.useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['landers', 'detail', landerId],
    queryFn: async (): Promise<{ lander: Lander }> => {
      const res = await api.api.landers[':id'].$get({ param: { id: landerId } });
      if (!res.ok) throw new Error(`Failed to load lander (${res.status})`);
      // The worker's `/api/landers/:id` GET returns a raw `Response` on its 403
      // RBAC branch (gateDomainRoleById), which collapses Hono's RPC body
      // inference to `{}` for this endpoint. The success shape is `{ lander }`;
      // annotate it here (the `Lander` type is inferred cleanly from the LIST
      // endpoint in components/landers/types.ts).
      return (await res.json()) as { lander: Lander };
    },
  });

  const lander = data?.lander;

  const saveMutation = useMutation({
    mutationFn: async (values: LanderFormValues) => {
      const body = buildUpdatePayload(values);
      const res = await api.api.landers[':id'].$patch(
        { param: { id: landerId } },
        { init: { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } } },
      );
      if (!res.ok) {
        if (res.status === 403) throw new Error("You don't have edit access to this lander.");
        throw new Error(`Save failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['landers'] });
      toast({ title: 'Lander saved', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: 'Could not save', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.landers[':id'].$delete({ param: { id: landerId } });
      if (!res.ok) {
        if (res.status === 403) throw new Error("You don't have edit access to this lander.");
        throw new Error(`Delete failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['landers'] });
      toast({ title: 'Lander deleted', variant: 'success' });
      void navigate({ to: '/landers' });
    },
    onError: (err: Error) => {
      toast({ title: 'Could not delete lander', description: err.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return <div className="p-8 text-sm text-gray-500">Loading lander…</div>;
  }
  if (isError || !lander) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-sm text-red-600">Failed to load this lander.</p>
        <Link to="/landers" className="text-sm text-gray-700 underline">
          Back to landers
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/landers" className="text-sm text-gray-500 hover:underline">
            ← Landers
          </Link>
          <h1 className="mt-2 font-mono text-xl font-semibold text-gray-900">{lander.slug}</h1>
        </div>
        {/* FF-26 (MIS-1009) — "Edit design" deep link. The builder param is the
            TEMPLATE id (live reference), never the lander id. A materialized
            puckJson with no templateId is a per-lander snapshot: there is no
            instance builder in scope, so show a hint instead of a link. */}
        {lander.templateId != null ? (
          <Link to="/templates/$id/builder" params={{ id: String(lander.templateId) }}>
            <Button variant="outline" size="sm">
              Edit design
            </Button>
          </Link>
        ) : lander.puckJson != null ? (
          <p className="text-sm text-gray-500">per-lander snapshot — edit via template</p>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lander settings</CardTitle>
        </CardHeader>
        <CardContent>
          <LanderForm
            mode="edit"
            defaultValues={landerToFormValues(lander)}
            isSubmitting={saveMutation.isPending}
            onSubmit={(values) => saveMutation.mutate(values)}
          />
        </CardContent>
      </Card>

      {/* Puck-7 (MIS-980) — per-lander click-to-call routing (number pool /
          static / disabled). Self-contained Card with its own PATCH; persists as
          `numberPool` on the lander row (injected as Puck metadata at render).
          'pool' saves also upsert the slug → pool mapping (needs the slug). */}
      <LanderRoutingSection
        landerId={lander.id}
        landerSlug={lander.slug}
        domainId={lander.domainId}
        value={lander.numberPool}
      />

      {/* Module R / R3 — attach a Meta ad account/campaigns to this lander for
          attribution. Self-contained (dark teal-mint themed) panel; RBAC + scope
          + binding cardinality are enforced by the /api/ad/* endpoints. */}
      <AttachMetaSourcePanel landerId={lander.id} />

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Danger zone</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            Deleting a lander removes it permanently. Its <span className="font-mono">/v/</span> URL
            will stop rendering.
          </p>
          <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
            Delete lander
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        ariaLabel="Confirm delete lander"
      >
        <DialogHeader>
          <DialogTitle>Delete lander</DialogTitle>
          <DialogDescription>This can't be undone.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">
            Delete <span className="font-mono font-medium">{lander.slug}</span>?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
